import { StringDevUIFieldDescriptor as UIFieldDescriptor } from "../../core/ui/StringDevUISchema";
import { GridAdapter } from "./GridAdapter";

/**
 * Rabatment adapter.
 *
 * Inscribes a square from the edge(s) of the rectangle.
 * The side of the square equals the shorter dimension of the element.
 *
 * In a landscape container the squares are anchored at the left
 * and/or right edges; in portrait — at the top and/or bottom.
 * The resulting vertical (or horizontal) lines are classic
 * compositional anchors used in painting and photography.
 * Optional diagonals of the inscribed squares reveal
 * additional focal points.
 */
export class RabatmentAdapter extends GridAdapter {
  readonly type = "rabatment";
  readonly label = "Rabatment";
  readonly icon = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="14" height="14" rx="0.5" stroke="currentColor" stroke-opacity="0.2"/><rect x="1" y="1" width="10" height="14" rx="0.3" stroke="currentColor" stroke-opacity="0.5"/><line x1="11" y1="1" x2="11" y2="15" stroke="currentColor" stroke-opacity="0.5" stroke-dasharray="2 1"/></svg>`;

  getDefaults(): Record<string, any> {
    return {
      side: "both",
      showDiagonals: false,
      color: "#56b886",
      opacity: 0.4,
    };
  }

  getUISchema(): UIFieldDescriptor[] {
    return [
      {
        type: "select",
        key: "side",
        label: "Side",
        default: "both",
        options: [
          { value: "both", label: "Both" },
          { value: "start", label: "Start" },
          { value: "end", label: "End" },
        ],
      },
      { type: "toggle", key: "showDiagonals", label: "Diagonals", default: false },
      { type: "divider" },
      { type: "color", key: "color", label: "Color", default: "#56b886" },
      { type: "range", key: "opacity", label: "Opacity", default: 0.4, min: 0, max: 1, step: 0.01 },
    ];
  }

  render(
    svg: SVGSVGElement,
    width: number,
    height: number,
    settings: Record<string, any>,
  ): void {
    const group = this.getGroup(svg, settings.__instanceId);
    const { side, showDiagonals, color, opacity } = settings;

    const isLandscape = width >= height;
    const s = Math.min(width, height);

    if (isLandscape) {
      this.renderLandscape(group, width, height, s, side, showDiagonals, color, opacity);
    } else {
      this.renderPortrait(group, width, height, s, side, showDiagonals, color, opacity);
    }
  }

  private renderLandscape(
    group: SVGGElement, w: number, h: number, s: number,
    side: string, diags: boolean, color: string, opacity: number,
  ): void {
    // Start (left) square
    if (side === "both" || side === "start") {
      // Square outline
      group.appendChild(this.createRect(0, 0, s, h, color, opacity * 0.15));
      // Rabatment line
      group.appendChild(this.createLine(s, 0, s, h, color, opacity, 1));

      if (diags) {
        group.appendChild(this.createLine(0, 0, s, h, color, opacity * 0.5, 0.8));
        group.appendChild(this.createLine(s, 0, 0, h, color, opacity * 0.5, 0.8));
      }
    }

    // End (right) square
    if (side === "both" || side === "end") {
      const x = w - s;
      group.appendChild(this.createRect(x, 0, s, h, color, opacity * 0.15));
      group.appendChild(this.createLine(x, 0, x, h, color, opacity, 1));

      if (diags) {
        group.appendChild(this.createLine(x, 0, w, h, color, opacity * 0.5, 0.8));
        group.appendChild(this.createLine(w, 0, x, h, color, opacity * 0.5, 0.8));
      }
    }
  }

  private renderPortrait(
    group: SVGGElement, w: number, h: number, s: number,
    side: string, diags: boolean, color: string, opacity: number,
  ): void {
    // Start (top) square
    if (side === "both" || side === "start") {
      group.appendChild(this.createRect(0, 0, w, s, color, opacity * 0.15));
      group.appendChild(this.createLine(0, s, w, s, color, opacity, 1));

      if (diags) {
        group.appendChild(this.createLine(0, 0, w, s, color, opacity * 0.5, 0.8));
        group.appendChild(this.createLine(w, 0, 0, s, color, opacity * 0.5, 0.8));
      }
    }

    // End (bottom) square
    if (side === "both" || side === "end") {
      const y = h - s;
      group.appendChild(this.createRect(0, y, w, s, color, opacity * 0.15));
      group.appendChild(this.createLine(0, y, w, y, color, opacity, 1));

      if (diags) {
        group.appendChild(this.createLine(0, y, w, h, color, opacity * 0.5, 0.8));
        group.appendChild(this.createLine(w, y, 0, h, color, opacity * 0.5, 0.8));
      }
    }
  }
}
